import { FunctionComponent, useEffect, useState } from "react";
import styles from "./BulkEditModal.module.css";
import { IoIosClose } from "react-icons/io";
import Dropdown from "../../DropDown/Dropdown";
import { contactResources, fetchConstants, fetchpropertySubType, bulkEdit, fetchLeadTasks } from "../../../Services/contacts";
import { connect, useDispatch } from 'react-redux';
import Loading from "../../Loading/Loading";
import Firebase from "firebase/app";
import { setRefreshContacts } from "../../../Redux/actions";
import Label from "../../../Components/Label";
import { themeColors } from "../../theme";
import { motion } from "framer-motion/dist/framer-motion";
import Backdrop from "../../Backdrop/Backdrop";
import { country_code, url } from "../../../Values/constants";
import Select from "react-select";
import { useLocation } from "react-router";

/* ------------------------------------------------------------------
   TYPES
-------------------------------------------------------------------*/
type props = {
  organization_id: string;
  close: () => void;
  open: boolean;
  rowsData: any[];
  theme: any;
  user: any;
};


/**
 * Animation variants for Framer Motion modal drop-in.
 */
const dropIn = {
  hidden: {
    y: "-100vh",
    opacity: 1,
  },
  visible: {
    y: "0",
    opacity: 1,
    transition: {
      duration: 1,
      type: "spring",
      damping: 25,
      stiffness: 500,
    },
  },
};

const BulkEditModal: FunctionComponent<props> = ({
  organization_id,
  close,
  open,
  rowsData,
  theme,
  user
}) => {
  const dispatcher = useDispatch();

  // ------------- SELECTED VALUES (FORM STATE) -----------------
  const [projectSelected, setProjectSelected] = useState<any>({ label: "Select", value: "Select" });
  const [locationSelected, setlocationSelected] = useState<any>({ label: "Select", value: "Select" });
  const [budgetSelected, setBudgetSelected] = useState<any>({ label: "Select", value: "Select" });
  const [propertyTypeSelected, setPropertyTypeSelected] = useState<any>({ label: "Select", value: "Select" });
  const [propertyStageSelected, setPropertyStageSelected] = useState<any>({ label: "Select", value: "Select" });
  const [leadSourceSelected, setLeadSourceSelected] = useState<any>({ label: "Select", value: "Select" });
  const [leadTypeSelected, setLeadTypeSelected] = useState<any>({ label: "Select", value: "Select" });

  // Country code dropdown
  const [countryCodeList, setCountryCodeList] = useState<string[]>([]);
  const [countryCode, setCountryCode] = useState<string>("");

  // ------------- MASTER DATA LISTS (OPTIONS) -------------------

  const [projectsList, setProjectsList] = useState<any[]>([]);
  const [locationsList, setLocationsList] = useState<any[]>([]);
  const [budgetsList, setBudgetsList] = useState<any[]>([]);
  const [propertyTypeList, setPropertyTypeList] = useState<any[]>([]);
  const [propertyStageList, setPropertyStageList] = useState<any[]>([]);
  const [notIntReasonList, setNotIntReasonList] = useState<any[]>([]);
  const [lostReasonList, setLostReasonList] = useState<any[]>([]);
  const [leadSourceList, setLeadSourceList] = useState<any[]>([]);
  const [leadTypeList, setleadTypeList] = useState<any[]>([]);
  const [comTypes, setComTypes] = useState<any[]>([]);
  const [resTypes, setResTypes] = useState<any[]>([]);

  // Object that accumulates changed values which will be applied in bulkEdit
  const [changeData, setChangeData] = useState<{ [key: string]: string }>({});

  // Loader state
  const [load, setLoad] = useState(false);

  // Tasks list used in bulk update
  const [tasksList, setTasksList] = useState<any[] | undefined>(undefined);

  // ------------------------------------------------------------
  // Build country code string list from constants
  // ------------------------------------------------------------
  useEffect(() => {
    let data: any[] = [];
    country_code.forEach((item) => {
      data.push(item.dial_code + " " + item.name);
    });
    setCountryCodeList(data);
  }, []);

  /**
 * Helper to convert countryCodeList to react-select options.
 */
  const choice = () => {
    let data = countryCodeList.map((item: any) => ({
      value: item,
      label: item,
    }));

    return data;
  };

  // ------------------------------------------------------------
  // Fetch contact related dropdown resources and constants
  // ------------------------------------------------------------
  useEffect(() => {
    // Projects, Budgets, Locations for the org
    const unsub = contactResources(
      user.organization_id,
      (data) => setLocationsList(data),
      (data) => setBudgetsList(data),
      (data) => setProjectsList(data),
    );

    // Not Interested, Lost, Property Stage, Property Type
    const unsubConst = fetchConstants(
      (data) => setNotIntReasonList(data),
      (data) => setLostReasonList(data),
      (data) => setPropertyStageList(data),
      (data) => setPropertyTypeList(data),
    );

    // Lead types (static here)
    let data = ["Data", "Leads"];
    setleadTypeList(data);

    // Property sub types & lead source list
    const unsubProperty = fetchpropertySubType(
      user.organization_id,
      (data) => setComTypes(data),
      (data) => setResTypes(data),
      (data) => setLeadSourceList(data)
    );

    return () => {
      unsub();
      unsubConst();
      unsubProperty();
    };
    // eslint-disable-next-line
  }, []);

  // ------------------------------------------------------------
  // Fetch tasks for each selected contact (rowsData)
  // ------------------------------------------------------------
  useEffect(() => {
    rowsData.forEach((item) => {
      fetchLeadTasks(
        (data) => setTasksList(data),
        item.contactId
      );
    })
  }, [rowsData]);

  // ------------------------------------------------------------
  // Apply Bulk Edit
  // ------------------------------------------------------------
  const apply = async () => {
    setLoad(true);

    // Passing modified_at timestamp along with updated fields
    await bulkEdit(
      { ...changeData, modified_at: Firebase.firestore.Timestamp.now() },
      rowsData,
      dispatcher,
      tasksList,
      (data) => setLoad(data),
      close,
    );
    setLoad(false);

    // Trigger contact refresh after slight delay
    setTimeout(() => {
      dispatcher(setRefreshContacts(true));
    }, 2500);
    close();
  };

  // Container styles for motion div (card)
  const containerStyles = { backgroundColor: theme ? themeColors.backgroundColor : themeColors.backgroundColorDark, color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark, maxHeight: "600px", overflowY: "auto" }

  /* ------------------------------------------------------------------
   RENDER
-------------------------------------------------------------------*/
  return (
    <>
      {load === true && <Loading />}
      <Backdrop>
        <motion.div
          key="modal"
          className="card p-4 shadow rounded"
          style={{
            position: "absolute",
            ...containerStyles
          }}
          variants={dropIn}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          {/* Header */}
          <div className={styles.firstDiv}>
            <h5>Mass Update Leads</h5>
            <div className={styles.cross} onClick={close}>
              <IoIosClose size={35} color={"#808080"} />
            </div>
          </div>
          <div className={styles.line}></div>
          <p className={styles.text}>
            Bulk edit allows you to apply same value to multiple records.
          </p>

          {/* Row: Project + Country Code */}
          <div className={styles.box2}>
            <div className={styles.divide}>
              <Label label="Project" required={false} />
              <div style={{ marginTop: "7px" }}>
                <Dropdown
                  option={projectsList}
                  selectedValue={projectSelected}
                  setSelectedValue={(value) => {
                    setProjectSelected(value);
                    changeData["project"] = value.value;
                    setChangeData(changeData);
                  }}
                />
              </div>
            </div>
            <div className={styles.divide}>
              <Label label="Country Code" required={false} />
              <div style={{ marginTop: "7px" }}>
                <Select
                  className={styles.select}
                  options={choice()}
                  onChange={(data) => {
                    if (data) {
                      setCountryCode(data.label);
                      let countryCode = data.label.split(" ")[0]
                      changeData["country_code"] = countryCode;
                      setChangeData(changeData);
                    }
                  }}
                  isSearchable={true}
                  value={{
                    label: countryCode,
                    value: countryCode,
                  }}
                  menuPlacement="auto"
                  maxMenuHeight={150}
                />
              </div>
            </div>
          </div>

          {/* Row: Location + Budget */}
          <div className={styles.box2}>
            <div className={styles.divide}>
              <Label label="Location" required={false} />
              <div style={{ marginTop: "7px" }}>
                <Dropdown
                  option={locationsList}
                  selectedValue={locationSelected}
                  setSelectedValue={(value) => {
                    setlocationSelected(value);
                    changeData["location"] = value.value;
                    setChangeData(changeData);
                  }}
                />
              </div>
            </div>
            <div className={styles.divide}>
              <Label label="Budget" required={false} />
              <div style={{ marginTop: "7px" }}>
                <Dropdown
                  option={budgetsList}
                  selectedValue={budgetSelected}
                  setSelectedValue={(value) => {
                    setBudgetSelected(value);
                    changeData["budget"] = value.value;
                    setChangeData(changeData);
                  }}
                />
              </div>
            </div>
          </div>

          {/* Row: Property Type + Property Stage */}
          <div className={styles.box2}>
            <div className={styles.divide}>
              <Label label="Property Type" required={false} />
              <div style={{ marginTop: "7px" }}>
                <Dropdown
                  option={propertyTypeList}
                  selectedValue={propertyTypeSelected}
                  setSelectedValue={(value) => {
                    setPropertyTypeSelected(value);
                    changeData["property_type"] = value.value;
                    setChangeData(changeData);
                  }}
                />
              </div>
            </div>
            <div className={styles.divide}>
              <Label label="Property Stage" required={false} />
              <div style={{ marginTop: "7px" }}>
                <Dropdown
                  option={propertyStageList}
                  selectedValue={propertyStageSelected}
                  setSelectedValue={(value) => {
                    setPropertyStageSelected(value);
                    changeData["property_stage"] = value.value;
                    setChangeData(changeData);
                  }}
                />
              </div>
            </div>
          </div>

          {/* Row: Lead Source + Lead Type */}
          <div className={styles.box2}>
            <div className={styles.divide}>
              <Label label="Lead Source" required={false} />
              <div style={{ marginTop: "7px" }}>
                <Dropdown
                  option={leadSourceList}
                  selectedValue={leadSourceSelected}
                  setSelectedValue={(value) => {
                    setLeadSourceSelected(value);
                    changeData["lead_source"] = value.value;
                    setChangeData(changeData);
                  }}
                />
              </div>
            </div>
            <div className={styles.divide}>
              <Label label="Lead Type" required={false} />
              <div style={{ marginTop: "7px" }}>
                <Dropdown
                  option={leadTypeList}
                  selectedValue={leadTypeSelected}
                  setSelectedValue={(value) => {
                    setLeadTypeSelected(value);
                    changeData["lead_type"] = value.value;
                    setChangeData(changeData);
                  }}
                />
              </div>
            </div>
          </div>

          {/* Apply button */}
          <button className={styles.apply} onClick={apply}>
            Apply
          </button>
        </motion.div>
      </Backdrop>
    </>
  );
};

/* ------------------------------------------------------------------
   REDUX
-------------------------------------------------------------------*/
const mapStateToProps = (state: any) => {
  return {
    user: state.user.data,
    theme: state.theme.isLightMode
  };
};
export default connect(mapStateToProps)(BulkEditModal);

